import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { API_BASE_URL } from '../../config/api';
import FinanceWorldCanvas from './components/FinanceWorldCanvas';
import { PublicProfileResponse } from './types/myWealth';
import './MyWealthPublicProfile.css';

export const MyWealthPublicProfile: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const [profile, setProfile] = useState<PublicProfileResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!username) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    const fetchPublicProfile = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const res = await fetch(`${API_BASE_URL}/finance-world/profile/${encodeURIComponent(username)}`);
        if (res.status === 404) {
          setNotFound(true);
          return;
        }
        const json = await res.json();
        if (json.success && json.data) {
          setProfile(json.data);
        } else {
          setNotFound(true);
        }
      } catch (err) {
        console.error('Error fetching public profile:', err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchPublicProfile();
  }, [username]);

  if (loading) {
    return (
      <div className="public-profile-page-wrapper">
        <div className="public-profile-card">
          <div className="canvas-spinner" />
          <p style={{ color: '#94a3b8' }}>Loading public 3D room...</p>
        </div>
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div className="public-profile-page-wrapper">
        <Helmet>
          <title>Character Profile Not Found — My Wealth</title>
        </Helmet>
        <div className="public-profile-card not-found-card">
          <div className="avatar-preview-badge">🔍</div>
          <h2>Profile Not Found</h2>
          <p style={{ color: '#94a3b8', maxWidth: 400 }}>
            The public character profile for <strong>@{username}</strong> could not be found or has
            been set to private by its creator.
          </p>
          <Link to="/app-store" className="action-btn secondary-btn" style={{ marginTop: 12 }}>
            Explore App Store
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="public-profile-page-wrapper">
      <Helmet>
        <title>{`${profile.displayName || profile.username}’s 3D World — My Wealth`}</title>
        <meta
          name="description"
          content={`Explore ${profile.displayName || profile.username}’s Habbo-inspired 3D decorative house on My Wealth.`}
        />
      </Helmet>

      {/* Profile Header Card */}
      <div className="public-header-bar">
        <div className="public-avatar-meta">
          <div
            className="avatar-preview-badge"
            style={{ backgroundColor: profile.shirtColor || '#38bdf8' }}
          >
            👤
          </div>
          <div className="avatar-info">
            <h1 className="profile-display-name">
              {profile.displayName || profile.username}
            </h1>
            <span className="profile-username-tag">@{profile.username}</span>
            {profile.bio && <p className="profile-bio-text">{profile.bio}</p>}
          </div>
        </div>

        <div className="public-nav-actions">
          <Link to="/finance-world" className="action-btn save-btn">
            🏰 Create Your Own 3D World
          </Link>
        </div>
      </div>

      {/* 3D World in Public View (Uses public theme & cosmetics only, NO financial data) */}
      <div className="public-world-canvas-container">
        <FinanceWorldCanvas
          isPublicView={true}
          publicTheme={profile.publicHouseTheme as any}
          characterCosmetics={{
            skinTone: profile.skinTone,
            hairStyle: profile.hairStyle as any,
            hairColor: profile.hairColor,
            shirtColor: profile.shirtColor,
            pantsColor: profile.pantsColor,
            shoesColor: profile.shoesColor,
            accessory: profile.accessory as any
          }}
          publicDecorations={profile.publicDecorations}
        />
      </div>

      {/* Cosmetic Details Showcase */}
      <div className="public-details-panel">
        <div className="details-card">
          <h4>🏡 Decorative House Theme</h4>
          <p className="theme-tag">
            {profile.publicHouseTheme ? profile.publicHouseTheme.toUpperCase() : 'CLASSIC'}
          </p>
          <span className="details-subtext">Curated Room & Furniture Styling</span>
        </div>

        <div className="details-card">
          <h4>🪴 Terrace Plants</h4>
          <p className="theme-tag">{profile.publicDecorations?.plantCount ?? 2} Houseplants</p>
          <span className="details-subtext">Terrace Botanical Garden</span>
        </div>

        <div className="details-card">
          <h4>🎨 Avatar Cosmetics</h4>
          <div className="color-dots-row">
            <span
              className="color-dot"
              style={{ backgroundColor: profile.shirtColor }}
              title="Shirt"
            />
            <span
              className="color-dot"
              style={{ backgroundColor: profile.pantsColor }}
              title="Pants"
            />
            <span
              className="color-dot"
              style={{ backgroundColor: profile.hairColor }}
              title="Hair"
            />
          </div>
          <span className="details-subtext">
            Accessory: {profile.accessory && profile.accessory !== 'none' ? profile.accessory : 'Standard'}
          </span>
        </div>
      </div>

      {/* Privacy Guarantee Note */}
      <div className="public-privacy-footer">
        🔒 All financial records, wealth calculations, and personal budgets on My Wealth are strictly
        private and never disclosed to public visitors.
      </div>
    </div>
  );
};

export default MyWealthPublicProfile;
